import { ChangeEvent, DragEvent, useEffect, useRef, useState } from "react";
import { DocumentUpload } from "iconsax-reactjs";
import { X } from "lucide-react";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "../../../shared/ui";
import { KybRequirement } from "../types";

type KybComplianceReUploadDocumentModalProps = {
  open: boolean;
  mode?: "add" | "reupload";
  document: KybRequirement | null;
  isSubmitting?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: { file: File; documentName?: string }) => void;
};

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_FILE_TYPES = ["image/jpeg", "image/png", "image/jpg", "application/pdf"];

const KybComplianceReUploadDocumentModal = ({
  open,
  mode = "reupload",
  document,
  isSubmitting,
  onOpenChange,
  onSubmit,
}: KybComplianceReUploadDocumentModalProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentName, setDocumentName] = useState("");
  const [fileError, setFileError] = useState("");
  const isAddMode = mode === "add";

  const resetState = () => {
    setSelectedFile(null);
    setDocumentName("");
    setFileError("");
    if (inputRef.current) inputRef.current.value = "";
  };

  useEffect(() => {
    if (!open) resetState();
  }, [open]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (isSubmitting) return;
    onOpenChange(nextOpen);
    if (!nextOpen) resetState();
  };

  const selectFile = (file?: File | null) => {
    if (!file) return;

    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      setSelectedFile(null);
      setFileError("Upload a JPEG, PNG, JPG, or PDF file.");
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setSelectedFile(null);
      setFileError("File size must be 5 MB or less.");
      return;
    }

    setSelectedFile(file);
    setFileError("");
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    selectFile(event.target.files?.[0]);
  };

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    selectFile(event.dataTransfer.files?.[0]);
  };

  const handleSubmit = () => {
    if (!selectedFile || isSubmitting) return;
    if (isAddMode && !documentName.trim()) return;
    onSubmit({ file: selectedFile, documentName: documentName.trim() });
  };

  return (
    <Modal
      open={open}
      onOpenChange={handleOpenChange}
      overlayClassName="bg-black/75"
      contentClassName="max-w-[680px] bg-white"
    >
      <ModalHeader className="items-center aku-heading-h2-semibold">
        <ModalTitle>
          {isAddMode ? "Add document" : "Re-upload document"}
        </ModalTitle>
        <button
          type="button"
          aria-label="Close modal"
          disabled={isSubmitting}
          onClick={() => handleOpenChange(false)}
          className="rounded-full p-2 text-[#161827] hover:bg-[#F2F4FF] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <X size={22} />
        </button>
      </ModalHeader>

      <ModalBody className="pt-4">
        {isAddMode ? (
          <div>
            <div
              id="kyb-add-document-name-label"
              className="aku-body-small-medium text-[#2C2F44]"
            >
              Document name
            </div>
            <input
              aria-labelledby="kyb-add-document-name-label"
              value={documentName}
              disabled={isSubmitting}
              onChange={(event) => setDocumentName(event.target.value)}
              placeholder="Enter document name"
              className="mt-2 h-12 w-full rounded-[14px] bg-[#EEF1FB] px-4 text-[14px] font-[350] text-gray-30 placeholder:text-gray-20 focus:outline-none focus:ring-2 focus:ring-[#D9DEFF] disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>
        ) : (
          <div className="aku-body-normal-medium text-[#4A4F73]">
            {document?.key || "Document"}
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept=".jpeg,.jpg,.png,.pdf,image/jpeg,image/png,application/pdf"
          className="hidden"
          disabled={isSubmitting}
          onChange={handleFileChange}
        />

        <button
          type="button"
          disabled={isSubmitting}
          onClick={() => inputRef.current?.click()}
          onDragOver={(event) => event.preventDefault()}
          onDrop={handleDrop}
          className="mt-4 flex w-full items-center gap-5 rounded-[14px] border border-dashed border-[#DDE2FA] bg-[#EEF1FB] px-5 py-6 text-left transition-colors hover:bg-[#E9ECF8] disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-[#D7C5FF] text-[#6F00FF]">
            <DocumentUpload size={26} color="#6F00FF" variant="Bold" />
          </span>
          <span className="min-w-0">
            <span className="block aku-body-normal-medium text-[#161827]">
              {selectedFile?.name || "Drag or Upload Document"}
            </span>
            <span className="mt-1 block aku-body-small-regular text-[#6F739B]">
              JPEG, PNG, JPG, PDF Max. file size: 5.0 MB
            </span>
          </span>
        </button>

        {fileError ? (
          <p className="mt-2 aku-body-small-regular text-[#C24E4E]">
            {fileError}
          </p>
        ) : null}
      </ModalBody>

      <ModalFooter>
        <Button
          type="button"
          variant="ghost"
          disabled={isSubmitting}
          onClick={() => handleOpenChange(false)}
          className="h-11 rounded-[12px] px-6 aku-body-normal-medium text-[#383B56] hover:bg-[#F7F8FD] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Cancel
        </Button>
        <Button
          type="button"
          disabled={
            !selectedFile || (isAddMode && !documentName.trim()) || isSubmitting
          }
          onClick={handleSubmit}
          className="h-11 rounded-[14px] px-6 text-[16px] font-[500]"
        >
          {isSubmitting ? "Uploading..." : "Upload"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default KybComplianceReUploadDocumentModal;
